import type { ReactNode } from 'react';
import { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import type { Creator } from '../../../types/creator.js';
import { CreatorStatus } from '../../../types/enums.js';
import { StatusDot } from '../../common/StatusDot';
import { fetchWithAuth } from '../../../lib/api/fetchWithAuth.js';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';
import { ModifyCreatorModal } from '../../modals/ModifyCreatorModal';

interface CreatorTableProps {
  creators: Creator[];
  onViewDetails: (creator: Creator) => void;
  onUpdate: () => Promise<void>;
  brandId: string;
  orgId: string;
}

// Add this type definition for filter configurations
type FilterConfig = {
  id: string;
  label: string;
  filter: (creator: Creator) => boolean;
};

export function CreatorTable({ 
  creators, 
  onViewDetails, 
  onUpdate,
  brandId,
  orgId
}: CreatorTableProps) {
  const [sortField, setSortField] = useState<string>('firstName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [activeFilter, setActiveFilter] = useState<string>('active');
  const { getAccessTokenSilently } = useAuth0();
  const [modifyModalOpen, setModifyModalOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);

  // Define the filter configurations
  const filterConfigs: FilterConfig[] = [
    {
      id: 'active',
      label: 'Active',
      filter: (creator: Creator) => 
        creator.status === CreatorStatus.ACTIVE
    },
    {
      id: 'suspended',
      label: 'Suspended',
      filter: (creator: Creator) => 
        creator.status === CreatorStatus.SUSPENDED
    }
  ];

  // Filter creators based on active filter
  const filteredCreators = creators.filter(creator => {
    const activeFilterConfig = filterConfigs.find(config => config.id === activeFilter);
    return activeFilterConfig ? activeFilterConfig.filter(creator) : true;
  });

  // Get count for each filter
  const filterCounts = filterConfigs.reduce((acc, config) => {
    acc[config.id] = creators.filter(config.filter).length;
    return acc;
  }, {} as Record<string, number>);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSuspend = async (creator: Creator, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const token = await getAccessTokenSilently();
      await fetchWithAuth(`/api/organizations/${orgId}/brands/${brandId}/creators/${creator.id}/suspend`, {
        method: 'POST',
        token
      });
      await onUpdate();
      toast.success('Creator suspended successfully');
    } catch (error) {
      console.error('Error suspending creator:', error);
      toast.error('Failed to suspend creator');
    }
  };

  const handleUnsuspend = async (creator: Creator, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const token = await getAccessTokenSilently();
      await fetchWithAuth(`/api/organizations/${orgId}/brands/${brandId}/creators/${creator.id}/unsuspend`, {
        method: 'POST',
        token
      });
      await onUpdate();
      toast.success('Creator unsuspended successfully');
    } catch (error) {
      console.error('Error unsuspending creator:', error);
      toast.error('Failed to unsuspend creator');
    }
  };

  const handleModify = (creator: Creator, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCreator(creator);
    setModifyModalOpen(true);
  };

  const sortedCreators = [...filteredCreators].sort((a, b) => {
    let aValue: string | number | Date | null;
    let bValue: string | number | Date | null;

    switch (sortField) {
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      case 'name':
        aValue = `${a.firstName} ${a.lastName || ''}`;
        bValue = `${b.firstName} ${b.lastName || ''}`;
        break;
      case 'tiktokPosts':
        aValue = a.tiktokPosts || 0;
        bValue = b.tiktokPosts || 0;
        return sortDirection === 'asc' ? (aValue || 0) - (bValue || 0) : (bValue || 0) - (aValue || 0);
      case 'tiktokViews':
        aValue = a.tiktokViews || 0;
        bValue = b.tiktokViews || 0;
        return sortDirection === 'asc' ? (aValue || 0) - (bValue || 0) : (bValue || 0) - (aValue || 0);
      default:
        aValue = a[sortField as keyof Creator] || '';
        bValue = b[sortField as keyof Creator] || '';
    }

    return sortDirection === 'asc'
      ? String(aValue || '').localeCompare(String(bValue || ''))
      : String(bValue || '').localeCompare(String(aValue || ''));
  });

  const SortableHeader = ({ 
    field, 
    children, 
    className 
  }: { 
    field: string, 
    children: ReactNode,
    className?: string 
  }) => (
    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider ${className || ''}`}>
      <button
        type="button"
        className="w-full flex items-center space-x-1 hover:text-gray-100"
        onClick={() => handleSort(field)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSort(field);
          }
        }}
      >
        <span>{children}</span>
        {sortField === field && (
          <span className="ml-1">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </button>
    </th>
  );

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl w-full h-[610px] flex flex-col">
      <div className="sticky top-0 z-20">
        <div className="flex space-x-1 p-2 bg-gray-900 rounded-t-lg">
          {filterConfigs.map(config => (
            <button
              type="button"
              key={config.id}
              onClick={() => setActiveFilter(config.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors
                ${activeFilter === config.id 
                  ? 'bg-indigo-600 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
                }`}
            >
              {config.label} ({filterCounts[config.id]})
            </button>
          ))}
        </div>
        <div className="bg-gray-900">
          <table className="w-full">
            <thead>
              <tr className="h-12">
                <SortableHeader field="status" className="w-16">Status</SortableHeader>
                <SortableHeader field="name" className="w-48">Name</SortableHeader>
                <SortableHeader field="email" className="w-64">Email</SortableHeader>
                <SortableHeader field="tiktokPosts" className="w-32">TikTok Posts</SortableHeader>
                <SortableHeader field="tiktokViews" className="w-32">TikTok Views</SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 tracking-wider w-16">Actions</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {sortedCreators.map((creator) => (
              <tr
                key={creator.id}
                className="hover:bg-gray-700 transition-colors cursor-pointer h-[60px]"
                onClick={() => onViewDetails(creator)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onViewDetails(creator);
                  }
                }}
                tabIndex={0}
                aria-label={`View details for ${creator.firstName} ${creator.lastName || ''}`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <StatusDot status={creator.status} />
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-100">
                    {`${creator.firstName} ${creator.lastName || ''}`}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-300">{creator.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-300">
                    {creator.tiktokPosts?.toLocaleString() || '0'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-300">
                    {creator.tiktokViews?.toLocaleString() || '0'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium relative">
                  <div className="relative group">
                    <div className="p-4 flex justify-center hover:bg-gray-700 rounded-lg">
                      <button 
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="text-gray-400 hover:text-gray-300"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>

                      <div className="absolute top-1/2 right-0 bg-gray-900 rounded-md shadow-lg py-1 hidden group-hover:block z-50" 
                           style={{ minWidth: '200px' }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewDetails(creator);
                          }}
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 w-full text-left"
                        >
                          View Details
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleModify(creator, e)}
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 w-full text-left"
                        >
                          Update Creator Details
                        </button>
                        {creator.status === CreatorStatus.ACTIVE && (
                          <button
                            type="button"
                            onClick={(e) => handleSuspend(creator, e)}
                            className="block px-4 py-2 text-sm text-red-400 hover:bg-gray-800 w-full text-left"
                          >
                            Suspend
                          </button>
                        )}
                        {creator.status === CreatorStatus.SUSPENDED && (
                          <button
                            type="button"
                            onClick={(e) => handleUnsuspend(creator, e)}
                            className="block px-4 py-2 text-sm text-green-400 hover:bg-gray-800 w-full text-left"
                          >
                            Unsuspend
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ModifyCreatorModal
        isOpen={modifyModalOpen}
        onClose={() => {
          setModifyModalOpen(false);
          setSelectedCreator(null);
        }}
        onCreatorModified={onUpdate}
        creator={selectedCreator}
        brandId={brandId}
        orgId={orgId}
      />
    </div>
  );
} 